// Orchestrates a scan into the data layer: engine raw response -> match student
// by 12-digit LRN within the assignment's class -> persist Result (scoped to
// the QuizAssignment) -> score against the quiz's key if one exists. This is the
// "scan -> match -> score -> store" wiring, reusable from the Scan screen.
import type { RawResponse } from '../engine/types.js';
import type { Repositories } from './repositories.js';
import { newId } from './repositories.js';
import type { QuizAssignmentRecord, ResultRecord, ResultItem } from './schema.js';
import { scoreResponse, type ScoreResult } from '../scoring/score.js';

export interface IngestOutcome {
  result: ResultRecord;
  scored: ScoreResult | null;
  /** the LRN that matched a roster student, or null when unmatched. */
  matchedLrn: string | null;
  reviewReasons: string[];
}

export async function ingestRawResponse(
  repos: Repositories,
  assignment: QuizAssignmentRecord,
  raw: RawResponse,
  opts: { capturedAt?: number; studentId?: string | null; capturedName?: string; capturedNameImage?: Blob | null } = {},
): Promise<IngestOutcome> {
  const rawAnswers: ResultItem[] = raw.answers.map((a) => ({ q: a.q, chosen: a.chosen, flags: a.flags }));
  const idDigitsRead = raw.studentNumber?.value ?? '';
  const mode = assignment.identificationMode ?? 'roster';

  const flaggedItems = rawAnswers.filter((a) => a.flags.length > 0).map((a) => a.q);
  const reviewReasons: string[] = [];

  let studentId: string | null = null;
  let matched: Awaited<ReturnType<Repositories['findByLrn']>> | undefined;
  let capturedName: string | null = null;

  if (mode === 'captured_name') {
    // §7A: no roster matching — the teacher types the handwritten name.
    capturedName = (opts.capturedName ?? '').trim() || null;
    if (!capturedName) reviewReasons.push('name not entered yet');
  } else {
    // roster: the sheet bubbles the 12-digit LRN directly; match it to a student
    // in this class. Only on a fully-confident read (12 digits, no ambiguous '?').
    const idIsClean = /^\d{12}$/.test(idDigitsRead);
    matched = idIsClean ? await repos.findByLrn(assignment.classId, idDigitsRead) : undefined;
    const assigned = opts.studentId !== undefined; // manual assignment overrides
    studentId = assigned ? (opts.studentId ?? null) : (matched?.id ?? null);
    if (!assigned) {
      if (!idIsClean) reviewReasons.push(`LRN unclear (${idDigitsRead || 'blank'})`);
      else if (!matched) reviewReasons.push(`no roster match for LRN ${idDigitsRead}`);
    }
  }
  if (flaggedItems.length) reviewReasons.push(`${flaggedItems.length} flagged item(s): ${flaggedItems.join(', ')}`);

  // TESTIFY-origin quizzes queue results for upload (outbox not built yet, §13.6);
  // captured_name results (no LRN) are never TESTIFY-eligible (§7A).
  const quiz = await repos.getQuiz(assignment.quizId);
  const uploadable = quiz?.origin === 'testify' && mode !== 'captured_name';

  const result: ResultRecord = {
    id: newId(),
    quizAssignmentId: assignment.id,
    studentId,
    capturedName,
    capturedNameImage: opts.capturedNameImage ?? null,
    idDigitsRead,
    rawAnswers,
    score: null,
    maxScore: null,
    needsReview: reviewReasons.length > 0,
    syncStatus: uploadable ? 'pending' : 'local_only',
    capturedAt: opts.capturedAt ?? Date.now(),
  };

  // score against the quiz's key if one exists; otherwise store raw for later
  const key = await repos.answerKeyFor(assignment.quizId);
  let scored: ScoreResult | null = null;
  if (key) {
    scored = scoreResponse(rawAnswers, key);
    result.score = scored.score;
    result.maxScore = scored.maxScore;
    if (scored.reviewItems.length && !result.needsReview) result.needsReview = true;
  }

  await repos.saveResult(result);
  return { result, scored, matchedLrn: matched ? idDigitsRead : null, reviewReasons };
}
